import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useMemo, useState} from 'react';

import {intersperseNodes} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {styled, useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {TabButtonElement} from '../../analytics/components/tabs/tabButtonElement';
import {InputDialog} from '../../components/common/dialogs/inputDialog';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {inboxHasColor} from '../../helpers/modelHelpers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {CompanyFeaturesEnum} from '../../models/api/companyModel';
import {InboxModel} from '../../models/api/inboxModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../models/url/settingsUrlModel';
import {getCompanyPreferences, getFindHasCompanyFeature} from '../../store/data/company/companySelectors';
import {deleteInboxAction} from '../../store/data/inboxes/inboxesActions';
import {getIsUserGlobalAdmin, getUserNamespace} from '../../store/data/teammates/teammatesSelectors';
import {makeUserIsAdminForTeamNamespace} from '../../store/data/teams/teamsSelectors';
import {getHasBusinessHoursInbox} from '../../store/featureFlags/featureFlagsSelectors';
import {useInboxBusinessHoursInfoBox} from '../businessHours/settingsBusinessHoursInfoBox';
import {SettingsBreadcrumbs} from '../common/settingsBreadcrumbs';
import {StyledSettingsFormHeaderButtons} from '../common/settingsWrapper';
import {useFetchChannels} from '../fetchers/channels';
import {SettingsHeader} from '../settingsHeader';
import {getWarningProps, getWarningsForChannel} from './settingsChannelWarningHelpers';

/*
 * Props.
 */

interface SettingsInboxesInboxHeaderProps {
  url: SettingsInboxesUrl;
  inbox: InboxModel;
}

/*
 * Styles.
 */

const StyledHeaderRightDiv = styled.div`
  ${flexCenter};
`;

const StyledDividerDiv = styled.div`
  width: 0;
  height: 30px;
  border-left: 1px solid ${(p) => p.theme.greys.shade40};
  margin: 0 10px 0 15px;
`;

/*
 * Component.
 */

export const SettingsInboxesInboxHeader: FC<SettingsInboxesInboxHeaderProps> = (props) => {
  const {url, inbox} = props;
  const {navigateToLink} = useLinkNavigation();

  const hasBusinessHoursInbox = useAppSelector(getHasBusinessHoursInbox);
  const findHasCompanyFeature = useAppSelector(getFindHasCompanyFeature);

  const userIsAdminForTeamNamespace = useMemo(
    () => makeUserIsAdminForTeamNamespace(url.namespace),
    [url.namespace],
  );
  const isUserTeamAdmin = useAppSelector(userIsAdminForTeamNamespace);

  // Business Hours are restricted to the Scale plan (must have teams) and to team admins.
  // Company also needs to have access to analytics OR advanced_rules (although it can be redundant with the 'teams' check, I'd rather be explicit here).
  const shouldShowBusinessHoursTab =
    hasBusinessHoursInbox &&
    isUserTeamAdmin &&
    findHasCompanyFeature(CompanyFeaturesEnum.TEAMS) &&
    (findHasCompanyFeature(CompanyFeaturesEnum.ADVANCED_RULES) ||
      findHasCompanyFeature(CompanyFeaturesEnum.ANALYTICS));

  const [isDeleteDialogOpen, setIsDialogOpen] = useState(false);
  const dispatch = useAppDispatch();
  const theme = useUserTheme();

  const {infoBoxIcon: businessHoursInfoBoxIcon} = useInboxBusinessHoursInfoBox();
  const userNamespace = useAppSelector(getUserNamespace);
  const companyPreferences = useAppSelector(getCompanyPreferences);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const {channels} = useFetchChannels(inbox.id);

  const channelWarnings = useMemo(
    () =>
      _(channels)
        .map((c) => getWarningsForChannel(c, userNamespace, companyPreferences))
        .flatten()
        .uniq()
        .value(),
    [channels, companyPreferences, userNamespace],
  );
  const channelWarningProps = useMemo(
    () => channelWarnings.map((warning) => [warning, getWarningProps(warning, isUserGlobalAdmin)] as const),
    [channelWarnings, isUserGlobalAdmin],
  );

  const tabsDefinition = [
    {
      key: 'overview',
      name: <Trans id="settings-inbox-tab-overview">Overview</Trans>,
      subPage: SettingsInboxesSubPagesEnum.OVERVIEW,
    },
    {
      key: 'channels',
      name: <Trans id="settings-inbox-tab-channels">Channels</Trans>,
      subPage: SettingsInboxesSubPagesEnum.CHANNELS,
      leftIcon: channelWarnings && channelWarnings.length > 0 && (
        <TooltipCoordinator
          render={() =>
            intersperseNodes(
              channelWarningProps.map(([warning, warningProps]) => ({
                node: warningProps.label,
                key: warning,
              })),
              () => <br />,
            )
          }
        >
          <Icon name="warningFilledSmall" color={theme.palette.orange.shade40} />
        </TooltipCoordinator>
      ),
    },
    {
      key: 'rules',
      name: <Trans id="settings-inbox-tab-rules">Rules</Trans>,
      subPage: SettingsInboxesSubPagesEnum.RULES,
    },
    {
      key: 'access',
      name: <Trans id="settings-inbox-tab-access">Teammate access</Trans>,
      subPage: SettingsInboxesSubPagesEnum.TEAMMATE_ACCESS,
    },
    {
      key: 'settings',
      name: <Trans id="settings-inbox-tab-settings">Settings</Trans>,
      subPage: SettingsInboxesSubPagesEnum.SETTINGS,
    },
    shouldShowBusinessHoursTab && {
      key: 'business-hours',
      name: <Trans id="settings-inbox-tab-business-hours">Business hours</Trans>,
      subPage: SettingsInboxesSubPagesEnum.BUSINESS_HOURS,
    },
    {
      key: 'custom-fields',
      name: <Trans id="settings-inbox-tab-custom-fields">Custom fields</Trans>,
      subPage: SettingsInboxesSubPagesEnum.CUSTOM_ATTRIBUTES,
    },
  ];

  const renderTitle = () => {
    if (!inbox) {
      return <Trans id="settings-singleinbox-header">Inbox</Trans>;
    }

    const iconColor = (inboxHasColor(inbox) && inbox.color) || theme.greys.shade70;

    return (
      <>
        <Icon color={iconColor} name="inboxFilled" />
        {inbox.name}
      </>
    );
  };

  const onConfirmDeleteInbox = async (toDeleteInbox: InboxModel) => {
    await dispatch(deleteInboxAction(toDeleteInbox.id));
    navigateToLink(
      buildSettingsPath({
        inboxId: undefined,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
        rootPage: SettingsRootPagesEnum.INBOXES,
        namespace: url.namespace,
      }),
    );
  };

  const maybeRenderDialog = () => {
    if (!isDeleteDialogOpen || !inbox) {
      return false;
    }

    return (
      <InputDialog
        title={<Trans id="settingsinboxes-delete-title">Delete inbox</Trans>}
        onValueSelect={() => onConfirmDeleteInbox(inbox)}
        okButtonLabel={<Trans id="settingsinboxes-delete-okbutton">Delete</Trans>}
        okButtonVariant={ButtonVariantsEnum.DANGER}
        onRequestClose={() => {
          setIsDialogOpen(false);
        }}
        checkIfOkIsDisabled={(value) => value !== inbox.name}
      >
        <Trans id="settingsinboxes-delete-copy1">
          You are deleting the <strong>{inbox.name}</strong> inbox.
        </Trans>
        <br />
        <br />
        <Trans id="settingsinboxes-delete-copy2">
          Once you delete an inbox, all its conversations will be gone forever. This action{' '}
          <strong>CANNOT</strong> be undone.
        </Trans>
        <br />
        <br />
        <Trans id="settingsinboxes-delete-copy3">
          Please type the name this inbox &quot;<strong>{inbox.name}</strong>&quot; to confirm.
        </Trans>
      </InputDialog>
    );
  };

  const renderHeaderRight = () => (
    <StyledHeaderRightDiv>
      <StyledSettingsFormHeaderButtons>
        <Button
          size={VisualSizesEnum.MEDIUM}
          variant={ButtonVariantsEnum.SECONDARY_DANGER}
          color={PaletteColorsEnum.RED}
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          <Trans id="settings-deleteinbox">Delete inbox</Trans>
        </Button>
      </StyledSettingsFormHeaderButtons>
      {url.subPage === SettingsInboxesSubPagesEnum.BUSINESS_HOURS ? (
        <>
          <StyledDividerDiv />
          {businessHoursInfoBoxIcon}
        </>
      ) : null}
    </StyledHeaderRightDiv>
  );

  const tabs = _(tabsDefinition)
    .compact()
    .map(({key, name, subPage, leftIcon}) => (
      <TabButtonElement
        key={key}
        name={name}
        leftIcon={leftIcon}
        isSelected={subPage === url.subPage}
        onTabClick={() =>
          navigateToLink(
            buildSettingsPath({
              ...url,
              resourcePage: SettingsResourcePagesEnum.EDIT,
              subPage,
            }),
          )
        }
      />
    ))
    .value();

  return (
    <>
      <SettingsHeader
        tabs={tabs}
        right={renderHeaderRight()}
        topLine={
          <SettingsBreadcrumbs
            showSeparatorAfterTopLevel
            breadcrumbs={[]}
            topLevelBreadcrumb={{
              label: <Trans id="settings-inboxes">Inboxes</Trans>,
              onBreadcrumbClick: () => {
                navigateToLink(
                  buildSettingsPath({
                    ...url,
                    subPage: undefined,
                    resourcePage: SettingsResourcePagesEnum.OVERVIEW,
                    channelId: undefined,
                    inboxId: inbox.id,
                  }),
                );
              },
            }}
          />
        }
      >
        {renderTitle()}
      </SettingsHeader>
      {maybeRenderDialog()}
    </>
  );
};
